import { useState } from 'react';
import config from '@app/config';
import { distinctValues } from '@app/utils/arrayUtils';

export interface StagedIds {
  activeIds: string[];
  addedIds: string[];
  removedIds: string[];
  changedIds: string[];
  hasChanges: boolean;
  maxChanges: number;
  exceedsMaxChanges: boolean;
  stageAdd(id: string): void;
  stageRemove(id: string): void;
  discard(): void;
}

const MAX_CHANGES_PER_SAVE = config.backee?.maxChangesPerSave ?? 100;

export default function useStagedIds(savedIds: string[]): StagedIds {
  const [activeIds, setActiveIds] = useState<string[]>(savedIds);

  // Keyed by content, since a caller may pass a new array with the same ids on every render
  const savedIdsKey = [...savedIds].sort().join('\n');
  const [stagedOnSavedIdsKey, setStagedOnSavedIdsKey] = useState(savedIdsKey);
  if (stagedOnSavedIdsKey !== savedIdsKey) {
    setStagedOnSavedIdsKey(savedIdsKey);
    setActiveIds(savedIds);
  }

  const addedIds = activeIds.filter((id) => !savedIds.includes(id));
  const removedIds = savedIds.filter((id) => !activeIds.includes(id));

  return {
    activeIds,
    addedIds,
    removedIds,
    changedIds: [...addedIds, ...removedIds],
    hasChanges: addedIds.length > 0 || removedIds.length > 0,
    maxChanges: MAX_CHANGES_PER_SAVE,
    exceedsMaxChanges: addedIds.length + removedIds.length > MAX_CHANGES_PER_SAVE,
    stageAdd: (id): void => setActiveIds((currentActiveIds) => distinctValues([...currentActiveIds, id])),
    stageRemove: (id): void => setActiveIds((currentActiveIds) => currentActiveIds.filter((activeId) => activeId !== id)),
    discard: (): void => setActiveIds(savedIds),
  };
}
